/**
 * An Export's progress, in its pill (Dax, 2026-10-04): a bar that fills as the worker reports (fetching,
 * encoding, measuring, uploading), striped and moving while it works, and the time it expects is left,
 * counted down between reports. The estimate leans long, so it finishes early; should it run out with the
 * render still going, it says so plainly instead of hanging on a number.
 */
import { useEffect, useState } from 'react';

const STAGE_LABELS: Record<string, string> = { cutting: 'Cutting', mixing: 'Mixing sound', joining: 'Joining', checking: 'Checking', fetching: 'Fetching footage', encoding: 'Rendering', measuring: 'Measuring sound', uploading: 'Uploading' };
/** How often the time left counts down between reports. */
const TICK_MS = 1000;
/** Under a minute, time left rounds up to this many seconds. */
const ROUND_S = 5;

function timeLeft(ms: number): string {
  const s = Math.ceil(ms / 1000);
  if (s < 60) return `about ${Math.max(ROUND_S, Math.ceil(s / ROUND_S) * ROUND_S)} s`;
  return `about ${Math.ceil(s / 60)} min`;
}

export function RenderPill({ status, stage, progress, etaMs }: { status: string; stage: string | null; progress: number; etaMs: number | null }) {
  // The time left as last reported, and when, so it counts down between reports.
  const [eta, setEta] = useState({ ms: etaMs, at: Date.now() });
  const [now, setNow] = useState(Date.now());
  useEffect(() => setEta({ ms: etaMs, at: Date.now() }), [etaMs]);
  const working = status === 'queued' || status === 'rendering';
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, [working]);
  if (!working) {
    return <span className={`chip${status === 'done' ? ' ok' : status === 'failed' ? ' bad' : ''}`}>{status === 'done' ? 'rendered' : status}</span>;
  }
  const left = eta.ms === null ? null : eta.ms - (now - eta.at);
  const label = status === 'queued' ? 'Queued'
    : left === null ? `${STAGE_LABELS[stage ?? ''] ?? 'Rendering'}…`
      : left > 0 ? `${Math.round(progress * 100)}% · ${timeLeft(left)}` : 'Almost there…';
  return (
    <span className="chip render-pill" role="status" title={STAGE_LABELS[stage ?? ''] ?? 'Rendering'}>
      <progress className="render-bar" value={status === 'queued' ? 0 : progress} max={1} aria-label="Export progress" />
      <span className="render-pill-label">{label}</span>
    </span>
  );
}
